import { useState, useEffect, useRef, useCallback } from 'react';
import authService from '../services/authService';

/**
 * 세션 만료 관리를 위한 커스텀 Hook
 * - 실시간 카운트다운 관리
 * - 세션 만료 알림 표시
 * - 토큰 검증
 * 
 * @param {Object} options
 * @param {Function} options.verifyAuth - SessionContext의 verifyAuth 함수
 * @param {number|null} options.expiresIn - 세션 만료까지 남은 시간 (초)
 * @param {Function} options.setExpiresIn - expiresIn 설정 함수
 * @param {boolean} options.isVerifying - 검증 중 상태
 * @returns {Object} { showExpirationAlert, remainingSeconds, handleExtendSession, handleLogout }
 */
export const useSessionExpiration = ({ verifyAuth, expiresIn, setExpiresIn, isVerifying }) => {
  const [showExpirationAlert, setShowExpirationAlert] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const countdownIntervalRef = useRef(null);
  const hasInitializedRef = useRef(false); // 초기화 추적

  // 세션 연장 핸들러 (?extend=1 로 강제 갱신)
  const handleExtendSession = useCallback(async () => {
    console.log('[useSessionExpiration] 세션 연장 시도');
    const result = await verifyAuth({ extend: true });
    if (result.success) {
      console.log('[useSessionExpiration] 세션 연장 성공 - 새로운 만료 시간:', result.expires_in);
      setShowExpirationAlert(false);
      if (result.expires_in !== undefined) {
        setRemainingSeconds(result.expires_in);
      }
      // SessionContext의 expiresIn은 verifyAuth에서 자동으로 업데이트됨
    }
    return result;
  }, [verifyAuth]);

  // 로그아웃 핸들러
  const handleLogout = useCallback(() => {
    setExpiresIn(null);
    setRemainingSeconds(null);
    setShowExpirationAlert(false);
    window.location.reload();
  }, [setExpiresIn]);

  // 로그아웃 상태 감지 및 처리
  useEffect(() => {
    if (!authService.isLoggedIn()) {
      setExpiresIn(null);
      setRemainingSeconds(null);
      setShowExpirationAlert(false);
    }
  }, [setExpiresIn]);

  // 실시간 카운트다운 (1초마다 감소)
  useEffect(() => {
    if (remainingSeconds === null || remainingSeconds <= 0) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    // Interval이 없을 때만 생성 (중복 방지)
    if (!countdownIntervalRef.current) {
      countdownIntervalRef.current = setInterval(() => {
        setRemainingSeconds(prev => {
          if (prev === null || prev <= 0) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            return null;
          }
          
          const newValue = prev - 1;

          // 알림은 "300초 초과 → 300초 이하"로 넘어갈 때만 표시 (연장 후 바로 재표시 방지)
          setShowExpirationAlert(prevAlert => {
            if (prev > 300 && newValue <= 300) return true;
            if (newValue > 300) return false;
            return prevAlert;
          });

          return newValue;
        });
      }, 1000);
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [remainingSeconds]);

  // ✅ 초기 만료 시간 설정 및 서버 동기화
  useEffect(() => {
    // 검증 완료되고, expiresIn이 있을 때
    if (!isVerifying && expiresIn !== null) {
      // remainingSeconds가 null이면 초기화 (초기 로딩 후에도 동작)
      if (remainingSeconds === null) {
        console.log('[useSessionExpiration] ✅ 초기 expiresIn 설정:', expiresIn, '초');
        setRemainingSeconds(expiresIn);
        hasInitializedRef.current = true;
        
        if (expiresIn <= 300) {
          setShowExpirationAlert(true);
        }
      } else if (hasInitializedRef.current) {
        // 이미 초기화된 경우, 서버 시간과 로컬 시간 차이가 5초 이상일 때만 동기화
        const diff = Math.abs(expiresIn - remainingSeconds);
        if (diff > 5) {
          setRemainingSeconds(expiresIn);
        }
      }
    }
    
    // 로그아웃 시 초기화
    if (expiresIn === null && hasInitializedRef.current) {
      console.log('[useSessionExpiration] ❌ 세션 종료 - 상태 리셋');
      setRemainingSeconds(null);
      setShowExpirationAlert(false);
      hasInitializedRef.current = false;
    }
  }, [isVerifying, expiresIn, remainingSeconds]);

  return {
    showExpirationAlert,
    remainingSeconds,
    handleExtendSession,
    handleLogout
  };
};
